import React, { useState } from 'react';
import { Send, AlertCircle, CheckCircle, Building2, CreditCard, User } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const PayoutRequestForm = ({ availableBalance, currency, walletId, userId, onSubmit, loading }) => {
  const [amount, setAmount] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const formatAmount = (val) => new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2 })?.format(val || 0);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (numAmount < 100) {
      setError('Minimum withdrawal amount is KES 100');
      return;
    }
    if (!bankName?.trim()) {
      setError('Please enter your bank name');
      return;
    }
    if (!accountNumber?.trim()) {
      setError('Please enter your account number');
      return;
    }
    if (!accountName?.trim()) {
      setError('Please enter the account holder name');
      return;
    }

    setSubmitting(true);
    try {
      // Server-side eligibility check: validates ledger balance and no pending request
      const { data: eligibility, error: eligibilityError } = await supabase?.rpc(
        'check_withdrawal_eligible',
        { p_wallet_id: walletId, p_amount: numAmount }
      );

      if (eligibilityError) {
        throw new Error(eligibilityError?.message);
      }

      await onSubmit?.({ amount: numAmount, bankName, accountNumber, accountName, walletId, userId });
      setSuccess(true);
      setAmount('');
      setBankName('');
      setAccountNumber('');
      setAccountName('');
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setError(err?.message || 'Failed to submit payout request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0d1f3c] rounded-2xl border border-white/10 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#ffd700]/20 flex items-center justify-center">
          <Send className="w-5 h-5 text-[#ffd700]" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Request Payout</h3>
          <p className="text-white/40 text-xs">Withdraw your earnings</p>
        </div>
      </div>
      {/* Available Balance */}
      <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10">
        <p className="text-white/40 text-xs mb-1">Available for withdrawal</p>
        <p className="text-[#ffd700] text-2xl font-bold">{currency || 'KES'} {formatAmount(availableBalance)}</p>
        <p className="text-white/30 text-xs mt-1">Admin approval required · Processed within 24-48 hours</p>
      </div>
      {success && (
        <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-3 mb-4">
          <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <p className="text-emerald-300 text-sm">Payout request submitted successfully! Awaiting admin approval.</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 bg-rose-500/20 border border-rose-500/30 rounded-xl p-3 mb-4">
          <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
          <p className="text-rose-300 text-sm">{error}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Amount */}
        <div>
          <label className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2 block">
            Withdrawal Amount ({currency || 'KES'})
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm font-medium">{currency || 'KES'}</span>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e?.target?.value)}
              placeholder="0.00"
              min="100"
              max={availableBalance}
              step="0.01"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-14 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#ffd700]/50 placeholder-white/20"
            />
          </div>
          <div className="flex gap-2 mt-2">
            {[25, 50, 75, 100]?.map(pct => (
              <button
                key={pct}
                type="button"
                onClick={() => setAmount((availableBalance * pct / 100)?.toFixed(2))}
                className="flex-1 py-1 text-xs bg-white/5 hover:bg-white/10 text-white/50 rounded-lg transition-colors"
              >
                {pct}%
              </button>
            ))}
          </div>
        </div>

        {/* Bank Name */}
        <div>
          <label className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2 block">Bank Name</label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              value={bankName}
              onChange={e => setBankName(e?.target?.value)}
              placeholder="e.g. Equity Bank, KCB, M-Pesa"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#ffd700]/50 placeholder-white/20"
            />
          </div>
        </div>

        {/* Account Number */}
        <div>
          <label className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2 block">Account Number / Phone</label>
          <div className="relative">
            <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              value={accountNumber}
              onChange={e => setAccountNumber(e?.target?.value)}
              placeholder="Account number or M-Pesa number"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#ffd700]/50 placeholder-white/20"
            />
          </div>
        </div>

        {/* Account Name */}
        <div>
          <label className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2 block">Account Holder Name</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              value={accountName}
              onChange={e => setAccountName(e?.target?.value)}
              placeholder="Full name as on account"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#ffd700]/50 placeholder-white/20"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting || loading || availableBalance <= 0}
          className="btn-primary-full disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
        >
          {submitting ? (
            <>
              <div className="w-4 h-4 border-2 border-[#0a1628]/30 border-t-[#0a1628] rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Request Payout
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default PayoutRequestForm;
